import { lazy, Suspense, useEffect, useMemo } from 'react';
import { ConfirmHost } from '../components/ui/ui';
import { chromeFor, resolveLook } from '../designs/look';
import { loadCustomFonts } from '../designs/customFonts';
import { EditorScreen } from '../editor/EditorScreen';
import { store, useStore } from '../state/store';
import { navigate, useRoute, type Route } from './router';
import { handleReminderAction, useScheduler } from './useScheduler';
import './app.css';

const HistoryScreen = lazy(() => import('../history/HistoryScreen').then((m) => ({ default: m.HistoryScreen })));
const VersionsScreen = lazy(() => import('../history/VersionsScreen').then((m) => ({ default: m.VersionsScreen })));
const SchedulesScreen = lazy(() => import('../scheduling/SchedulesScreen').then((m) => ({ default: m.SchedulesScreen })));
const ScheduleEditor = lazy(() => import('../scheduling/ScheduleEditor').then((m) => ({ default: m.ScheduleEditor })));
const UpcomingScreen = lazy(() => import('../scheduling/UpcomingScreen').then((m) => ({ default: m.UpcomingScreen })));
const PreviewScreen = lazy(() => import('../lockscreen/PreviewScreen').then((m) => ({ default: m.PreviewScreen })));
const SettingsScreen = lazy(() => import('../settings/SettingsScreen').then((m) => ({ default: m.SettingsScreen })));
const TemplatesScreen = lazy(() => import('../notes/TemplatesScreen').then((m) => ({ default: m.TemplatesScreen })));

/** Track the visible viewport height so the editor stays above the on-screen keyboard (iOS does not resize the layout). */
function useVisualViewport() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => document.documentElement.style.setProperty('--vvh', `${Math.round(vv.height)}px`);
    update();
    vv.addEventListener('resize', update);
    return () => vv.removeEventListener('resize', update);
  }, []);
}

/** The app chrome takes its colours from the note in focus, like the reference app. */
function useChrome(route: Route) {
  const notes = useStore((s) => s.notes);
  const designs = useStore((s) => s.designs);
  const activeId = useStore((s) => s.active.noteId);
  const id = route.name === 'note' || route.name === 'versions' ? route.id : activeId;
  const note = id ? notes[id] : undefined;
  const chrome = useMemo(() => (note ? chromeFor(resolveLook(note, designs)) : null), [note, designs]);
  useEffect(() => {
    const root = document.documentElement.style;
    if (!chrome) return;
    root.setProperty('--app-bg', chrome.bg);
    root.setProperty('--app-fg', chrome.fg);
    root.setProperty('--app-icon', chrome.icon);
    root.setProperty('--app-rim', chrome.rim);
    root.setProperty('--app-sheet-bg', chrome.sheetBg);
    root.setProperty('--app-sheet-fg', chrome.sheetFg);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', chrome.bg);
    document.documentElement.style.colorScheme = chrome.dark ? 'dark' : 'light';
  }, [chrome]);
}

function Toasts() {
  const toasts = useStore((s) => s.toasts);
  return (
    <div className="toasts no-print" aria-live="polite" role="status">
      {toasts.map((t) => (
        <div key={t.id} className={`toast is-${t.tone}`}>
          <span className="toast-text">{t.text}</span>
          {t.action && (
            <button
              type="button"
              onClick={() => {
                t.action!.run();
                store.dismissToast(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
          <button type="button" aria-label="Dismiss" onClick={() => store.dismissToast(t.id)}>
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

function ReminderBanner() {
  const inbox = useStore((s) => s.inbox);
  const item = inbox[0];
  if (!item) return null;
  return (
    <div className="reminder-banner no-print" role="alertdialog" aria-labelledby="rb-title" aria-describedby="rb-body">
      <h2 id="rb-title">
        {item.missed ? 'Missed: ' : '⏰ '}
        {item.title}
      </h2>
      <p id="rb-body">
        {item.body} · {new Date(item.occurrence).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}
        {inbox.length > 1 ? ` · +${inbox.length - 1} more` : ''}
      </p>
      <div className="reminder-actions">
        <button
          type="button"
          className="btn is-small is-primary"
          onClick={() => {
            void handleReminderAction('open', item.key, item.scheduleId);
            navigate({ name: 'note', id: item.noteId });
          }}
        >
          Open note
        </button>
        {!item.missed && (
          <button type="button" className="btn is-small" onClick={() => void handleReminderAction('snooze', item.key, item.scheduleId)}>
            Snooze 10 min
          </button>
        )}
        <button type="button" className="btn is-small" onClick={() => void handleReminderAction('done', item.key, item.scheduleId)}>
          Done
        </button>
        <button type="button" className="btn is-small" onClick={() => void handleReminderAction('dismiss', item.key, item.scheduleId)}>
          Dismiss
        </button>
      </div>
    </div>
  );
}

function NewNoteRedirect() {
  useEffect(() => {
    const n = store.createNote();
    navigate({ name: 'note', id: n.id }, true);
  }, []);
  return null;
}

export function App() {
  const ready = useStore((s) => s.ready);
  const fatal = useStore((s) => s.fatal);
  const activeId = useStore((s) => s.active.noteId);
  const route = useRoute();
  useChrome(route);
  useScheduler(ready && !fatal);
  useVisualViewport();

  useEffect(() => {
    if (ready) void loadCustomFonts();
  }, [ready]);

  // Save on the way out (tab switch, app switch, close).
  useEffect(() => {
    const flush = () => void store.flush();
    const onVis = () => document.visibilityState === 'hidden' && flush();
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Notification actions forwarded by the service worker, or passed in the URL when it opened a new window.
  useEffect(() => {
    if (!ready) return;
    const params = new URLSearchParams(location.search);
    const na = params.get('na');
    if (na && params.get('nk') && params.get('ns')) {
      void handleReminderAction(na as 'snooze' | 'done' | 'dismiss' | 'open', params.get('nk')!, params.get('ns')!);
      history.replaceState(null, '', location.pathname + location.hash);
    }
    const onMsg = (e: MessageEvent) => {
      if (e.data?.type === 'notification-action') void handleReminderAction(e.data.action, e.data.key, e.data.scheduleId);
    };
    navigator.serviceWorker?.addEventListener('message', onMsg);
    return () => navigator.serviceWorker?.removeEventListener('message', onMsg);
  }, [ready]);

  if (!ready) {
    return (
      <div className="boot" role="status" aria-live="polite">
        <span className="sr-only">Loading MemoNest…</span>
      </div>
    );
  }

  let screen: React.ReactNode;
  switch (route.name) {
    case 'note':
      screen = <EditorScreen key={route.id} noteId={route.id} />;
      break;
    case 'new':
      screen = <NewNoteRedirect />;
      break;
    case 'history':
      screen = <HistoryScreen />;
      break;
    case 'versions':
      screen = <VersionsScreen noteId={route.id} />;
      break;
    case 'schedules':
      screen = <SchedulesScreen noteId={route.noteId} />;
      break;
    case 'schedule':
      screen = <ScheduleEditor key={route.id} id={route.id} />;
      break;
    case 'upcoming':
      screen = <UpcomingScreen />;
      break;
    case 'preview':
      screen = <PreviewScreen />;
      break;
    case 'settings':
      screen = <SettingsScreen />;
      break;
    case 'templates':
      screen = <TemplatesScreen />;
      break;
    default:
      screen = activeId ? <EditorScreen key={activeId} noteId={activeId} /> : <NewNoteRedirect />;
  }

  return (
    <>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      {fatal && (
        <div className="fatal-banner" role="alert">
          {fatal}
        </div>
      )}
      <main id="main">
        <Suspense fallback={<div className="boot" />}>{screen}</Suspense>
      </main>
      <ReminderBanner />
      <Toasts />
      <ConfirmHost />
    </>
  );
}
